
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ExternalLink, Timer, CheckCircle, XCircle } from 'lucide-react';
import { FairClickSite } from '@/types/fairclick';

interface SiteVisitCardProps {
  site: FairClickSite | null;
  onVisitComplete: (siteId: string, duration: number) => Promise<boolean>;
  onGetNewSite: () => Promise<FairClickSite | null>;
  isLoading: boolean;
}

export const SiteVisitCard = ({ site, onVisitComplete, onGetNewSite, isLoading }: SiteVisitCardProps) => {
  const [visitStarted, setVisitStarted] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [visitComplete, setVisitComplete] = useState(false);
  const [visitSuccess, setVisitSuccess] = useState(false);
  const [validating, setValidating] = useState(false);
  const timerRef = useRef<number | null>(null);
  const targetDuration = 10; // 10 secondes requises
  
  // Réinitialiser l'état lors du changement de site
  useEffect(() => {
    setVisitStarted(false);
    setTimeElapsed(0);
    setVisitComplete(false);
    setVisitSuccess(false);
    
    // Nettoyer le timer
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [site]);
  
  // Gérer le démarrage de la visite
  const handleStartVisit = () => {
    if (!site) return;
    
    setVisitStarted(true);
    setTimeElapsed(0);
    
    // Ouvrir le site dans un nouvel onglet avec utm_source
    let urlWithUtm = site.url;
    const hasParams = urlWithUtm.includes('?');
    urlWithUtm += hasParams ? '&utm_source=alunatools' : '?utm_source=alunatools';
    
    window.open(urlWithUtm, '_blank');
    
    // Démarrer le chronomètre
    timerRef.current = window.setInterval(() => {
      setTimeElapsed(prev => {
        const newTime = prev + 1;
        
        // Arrêter automatiquement après 20 secondes (laissons un peu de marge au-delà des 10s requises)
        if (newTime >= 20) {
          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
          
          setVisitComplete(true);
          return 20;
        }
        
        // Marquer comme complété après le temps requis
        if (newTime >= targetDuration && !visitComplete) {
          setVisitComplete(true);
        }
        
        return newTime;
      });
    }, 1000);
  };
  
  // Valider la visite
  const handleValidateVisit = async () => {
    if (!site || !visitComplete || validating) return;
    
    setValidating(true);
    
    try {
      // Nettoyer le timer si ce n'est pas déjà fait
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Utilisation de console.log pour déboguer
      console.log('Validation de la visite', site.id, timeElapsed);
      
      const success = await onVisitComplete(site.id, timeElapsed);
      console.log('Résultat de la validation:', success);
      
      setVisitSuccess(success);
    } catch (error) {
      console.error('Erreur lors de la validation de la visite:', error);
      setVisitSuccess(false);
    } finally {
      setValidating(false);
    }
  };
  
  const progressPercentage = visitComplete 
    ? 100 
    : Math.min(Math.round((timeElapsed / targetDuration) * 100), 100);
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>{site ? 'Visitez ce site' : 'Aucun site à visiter'}</span>
          {site && (
            <Timer 
              size={20} 
              className={visitComplete ? 'text-green-500' : 'text-orange-500'} 
            />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {site ? (
          <>
            <div className="flex flex-col space-y-2">
              <h3 className="text-xl font-semibold">{site.title}</h3>
              <a 
                href={site.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-500 hover:underline flex items-center gap-1"
              >
                {site.url} <ExternalLink size={14} />
              </a>
              {site.description && (
                <p className="text-sm text-gray-600 mt-2">{site.description}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progression</span>
                <span>
                  {timeElapsed}/{targetDuration} secondes
                </span>
              </div>
              <Progress value={progressPercentage} />
            </div>
            
            {visitSuccess && (
              <div className="flex items-center p-3 bg-green-50 text-green-700 rounded-md">
                <CheckCircle className="mr-2 h-5 w-5" />
                <span>
                  Visite validée ! Vous avez gagné 1 crédit.
                </span>
              </div>
            )}
            
            {visitComplete && !visitSuccess && validating === false && (
              <div className="flex items-center p-3 bg-yellow-50 text-yellow-700 rounded-md">
                <CheckCircle className="mr-2 h-5 w-5" />
                <span>
                  Visite terminée ! Cliquez sur valider pour recevoir votre crédit.
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="py-6 text-center">
            <p className="text-gray-500">
              {isLoading 
                ? "Recherche d'un site à visiter..." 
                : "Aucun site n'est disponible pour le moment. Réessayez plus tard ou ajoutez votre propre site pour amorcer le système."}
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between gap-2">
        {site ? (
          <>
            {!visitStarted ? (
              <Button onClick={handleStartVisit} className="w-full">
                Visiter ce site
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleValidateVisit}
                  disabled={!visitComplete || validating || visitSuccess}
                  className="w-full"
                >
                  {validating ? "Validation..." : "Valider ma visite"}
                </Button>
                
                {!visitSuccess && (
                  <Button
                    variant="outline"
                    onClick={() => onGetNewSite()}
                    disabled={isLoading}
                    className="w-full"
                  >
                    Passer ce site
                  </Button>
                )}
              </>
            )}
          </>
        ) : (
          <Button 
            onClick={() => onGetNewSite()} 
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? "Recherche..." : "Trouver un site à visiter"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
